/**
 * Presentation helpers for the in-flight DET workflow progress indicator.
 *
 * The backend reports progress as raw DET step-kind strings (see
 * `agent/workflows/steps/*.py` `@step_kind(...)`). These are internal and
 * not user-friendly, so the chat UI maps each kind to one of a handful of
 * high-level "thinking" stages (label + detail) shown while a query runs.
 */

/**
 * Maps DET `StepKind` strings to user-facing labels shown as the primary
 * progress line. Any kind not listed here falls back to `friendlyStepLabel`,
 * which de-camel-cases the raw kind.
 */
export const STEP_LABELS: Record<string, string> = {
    DeviseAPlan: 'Devising a plan',
    ValidatePlan: 'Validating the plan',
    InformUserUnanswerable: 'Checking what can be answered',
    GatherFactoidsAndPresent: 'Gathering case knowledge',
    ExecuteDAGQuestion: 'Querying the data',
    EvaluateDAGQuestion: 'Evaluating the results',
    ReviewQuestionResults: 'Reviewing the findings',
    PersistResultsAndAdvance: 'Saving progress',
    SynthesizeFinalResponse: 'Writing the answer',
    GenerateConversationalResponse: 'Composing a reply',
    SemiManualQuery: 'Building the query',
    SemiManualExecute: 'Running the query',
    SemiManualDeliver: 'Preparing the results',
};

/**
 * High-level stages presented to the user during an in-flight query. Each DET
 * step kind maps to one of these via {@link STEP_KIND_TO_THINKING}; the chat
 * progress indicator shows the matched stage's `label` (primary line) and
 * `detail` (muted line).
 */
export interface ThinkingStep {
    id: string;
    label: string;
    detail: string;
}

export const THINKING_STEPS: ThinkingStep[] = [
    {
        id: 'decompose',
        label: 'Decomposing the question',
        detail: 'Breaking down intent, extracting key entities and constraints',
    },
    {
        id: 'skills',
        label: 'Reading skills',
        detail: 'Loading relevant domain knowledge and reasoning frameworks',
    },
    {
        id: 'search',
        label: 'Searching evidence',
        detail: 'Querying knowledge sources and cross-referencing data points',
    },
    {
        id: 'review',
        label: 'Reviewing evidence',
        detail: 'Evaluating source quality, resolving contradictions, filtering noise',
    },
    {
        id: 'summarize',
        label: 'Summarizing findings',
        detail: 'Synthesizing insights into a coherent, structured response',
    },
];

/**
 * Maps each DET step kind to a {@link THINKING_STEPS} stage id. Kinds not
 * listed fall back to the first stage (see {@link thinkingStepForKind}).
 */
const STEP_KIND_TO_THINKING: Record<string, string> = {
    // Planning the question.
    DeviseAPlan: 'decompose',
    ValidatePlan: 'decompose',
    InformUserUnanswerable: 'decompose',
    // Pulling in case knowledge / domain context.
    GatherFactoidsAndPresent: 'skills',
    // Retrieving evidence from the data.
    ExecuteDAGQuestion: 'search',
    SemiManualQuery: 'search',
    SemiManualExecute: 'search',
    // Assessing what came back.
    EvaluateDAGQuestion: 'review',
    ReviewQuestionResults: 'review',
    PersistResultsAndAdvance: 'review',
    // Composing the response.
    SynthesizeFinalResponse: 'summarize',
    GenerateConversationalResponse: 'summarize',
    SemiManualDeliver: 'summarize',
};

/**
 * Resolves a DET step kind to the thinking stage shown to the user. Falls back
 * to the first stage when no step is reported yet or the kind is unmapped, so
 * the indicator always renders a sensible label + detail.
 */
export function thinkingStepForKind(kind: string | null | undefined): ThinkingStep {
    const id = kind ? STEP_KIND_TO_THINKING[kind] : undefined;
    return THINKING_STEPS.find((s) => s.id === id) ?? THINKING_STEPS[0];
}

/**
 * Returns a readable label for a DET step kind. Falls back to splitting a
 * CamelCase kind into words (e.g. `"FooBarBaz"` -> `"Foo bar baz"`) so a
 * newly added step still renders something sensible without a code change.
 */
export function friendlyStepLabel(kind: string | null | undefined): string {
    if (!kind) return 'Working';
    const known = STEP_LABELS[kind];
    if (known) return known;
    const spaced = kind.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
    if (!spaced) return 'Working';
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
